import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Menu, MenuItem, Tooltip, IconButton, Stack, styled } from '@mui/material';
import React, { useState, MouseEvent } from 'react';
import { Cell } from 'react-table';
import {
  deleteCandidateDataDownloadSettingsTemplate,
  deleteDataDownloadSettingsTemplate,
  favoriteCandidateDataDownloadSettingsTemplate,
  favoriteDataDownloadSettingsTemplate,
  hideCandidateDataDownloadSettingsTemplate,
  hideDataDownloadSettingsTemplate,
  IJobsDownloadTemplate,
} from 'wisestep-api';
import {
  WsColors,
  WsDataTable,
  Item,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsButton,
  timeSince,
  IUserDTO,
} from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../../context';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const StyledStack = styled(Stack)({
  padding: '2',
  backgroundColor: WsColors.TheBlues50,
  gap: 3,
});
const Styles = styled(Stack)({ padding: '0', gap: 0.5 });

interface TemplateListingProps {
  templates: IJobsDownloadTemplate[];
  getLatestData: () => void;
  handleDrawer: (type: string) => void;
  getFlagType: (flag: 'new' | 'edit' | 'clone') => void;
  getEditorCloneId: (id: string) => void;
}

const JobsTemplateListingTable = ({
  templates,
  getLatestData,
  handleDrawer,
  getFlagType,
  getEditorCloneId,
}: TemplateListingProps) => {
  const [bool, setBool] = useState(false);
  const handleCloseOptions = () => setAnchorEl(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const open = Boolean(anchorEl);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspaceContext = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const favoriteTemplateHandler = async (cell: Cell, flag: boolean) => {
    const template = cell.row.original as IJobsDownloadTemplate;
    try {
      template.dataType === 'Jobs'
        ? await favoriteDataDownloadSettingsTemplate(template.id ?? '', flag)
        : await favoriteCandidateDataDownloadSettingsTemplate(template.id ?? '', flag);
      setNotification(success('Successful'));
      getLatestData();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const hideTemplateHandler = async (cell: Cell, flag) => {
    const template = cell.row.original as IJobsDownloadTemplate;
    try {
      template.dataType === 'Jobs'
        ? await hideDataDownloadSettingsTemplate(template.id ?? '', flag)
        : await hideCandidateDataDownloadSettingsTemplate(template.id ?? '', flag);
      setNotification(success('Successful'));
      getLatestData();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const deleteTemplateHandler = async (cell: Cell) => {
    const template = cell.row.original as IJobsDownloadTemplate;
    if (template.recruiterId != 'DEFAULT') {
      try {
        template.dataType === 'Jobs'
          ? await deleteDataDownloadSettingsTemplate(template.id ?? '')
          : await deleteCandidateDataDownloadSettingsTemplate(template.id ?? '');
        setNotification(success('Deleted Successfully'));
        getLatestData();
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    } else {
      setNotification(warning('You Cannot Delete Default Template'));
    }
  };

  const editTemplate = (cell: Cell) => {
    const template = cell.row.original as IJobsDownloadTemplate;
    getFlagType('edit');
    template.id && getEditorCloneId(template?.id);
    handleDrawer(template.dataType === 'Jobs' ? 'jobs' : 'CRM');
  };

  const cloneTemplate = (cell: Cell) => {
    const template = cell.row.original as IJobsDownloadTemplate;
    getFlagType('clone');
    template.id && getEditorCloneId(template?.id);
    handleDrawer(template.dataType === 'Jobs' ? 'jobs' : 'CRM');
  };

  const templateData = templates?.map((templatesList) => ({
    title: templatesList.name,
    recruiterId: templatesList.recruiterId,
    createdOn: String(timeSince(String(templatesList.createdOn))),
    dataType: templatesList.dataType,
    id: templatesList.id,
  }));

  const columns = [
    {
      Header: 'Title',
      accessor: 'title',
    },
    {
      Header: 'Created by',
      accessor: 'recruiterId',
      Cell: (cell: Cell) => {
        return <WsTypography color={WsTypoColors.SENARY}>{cell.value}</WsTypography>;
      },
    },
    {
      Header: 'Created On',
      accessor: 'createdOn',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },
    {
      Header: 'Type',
      accessor: 'dataType',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },
    {
      Header: 'Actions',
      id: 'actions',
      Cell: (cell: Cell) => {
        if (
          templates[cell.row.index]?.recruiterId === (loggedInUser?.email as string) ||
          (loggedInUser?.email as string) === (workspaceContext?.ownerId as string)
        ) {
          setBool(true);
        }
        return (
          <Styles padding={0} direction="row">
            <WsTypography color={WsTypoColors.PRIMARY}>{cell.value}</WsTypography>
            <Item>
              <Tooltip title={'Star'}>
                {templates[cell.row.index]?.isFavorite ? (
                  <IconButton onClick={() => favoriteTemplateHandler(cell, false)}>
                    <StarIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => favoriteTemplateHandler(cell, true)}>
                    <StarBorderIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </IconButton>
                )}
              </Tooltip>
            </Item>
            {bool === true && (
              <Item>
                <IconButton onClick={() => editTemplate(cell)}>
                  <Tooltip title={'Edit'}>
                    <EditIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              </Item>
            )}
            <Item>
              {templates[cell.row.index]?.isHidden ? (
                <IconButton onClick={() => hideTemplateHandler(cell, false)}>
                  <Tooltip title={'Show/Hide'}>
                    <VisibilityOffIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              ) : (
                <IconButton onClick={() => hideTemplateHandler(cell, true)}>
                  <Tooltip title={'Show/Hide'}>
                    <VisibilityIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                  </Tooltip>
                </IconButton>
              )}
            </Item>
            <Item>
              <IconButton onClick={() => cloneTemplate(cell)}>
                <Tooltip title={'Clone'}>
                  <FileCopyIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} />
                </Tooltip>
              </IconButton>
            </Item>
            <Item onClick={() => deleteTemplateHandler(cell)}>
              <Tooltip title={'Delete'}>
                <DeleteIcon sx={{ fill: WsColors.Red500, cursor: 'pointer' }} />
              </Tooltip>
            </Item>
          </Styles>
        );
      },
    },
  ];

  const onCreateTemplate = (type: string) => {
    getFlagType('new');
    handleDrawer(type);
    handleCloseOptions();
  };

  return (
    <StyledStack>
      <Stack direction="row" justifyContent={'space-between'}>
        <Item>
          <WsTypography variant={WsTypoVariants.TITLE}>Template List</WsTypography>
        </Item>
        <Item>
          <WsButton onClick={handleClick} variant="contained" size="medium">
            Create New Template
          </WsButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleCloseOptions}>
            <MenuItem onClick={() => onCreateTemplate('crm')}>CRM Settings</MenuItem>
            <MenuItem onClick={() => onCreateTemplate('jobs')}>Jobs Settings</MenuItem>
          </Menu>
        </Item>
      </Stack>
      <WsDataTable columns={columns} data={templateData} />
    </StyledStack>
  );
};
export default JobsTemplateListingTable;
